import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { Card, Field, inputCls } from "../ui";
import { FilePicker } from "../../components/QuoteSection";

const TYPES = [
  { value: "one_time", label: "One-Time Supply", hint: "A single shipment or delivery" },
  { value: "market_check", label: "Market Check", hint: "Benchmark a price or test availability before you commit" },
  { value: "recurring", label: "Recurring Supply", hint: "Ongoing managed procurement" },
  { value: "multi_location", label: "Multi-Location Supply", hint: "One program, several delivery locations" },
  { value: "international", label: "International Procurement", hint: "International company procuring in Nigeria" },
];

const PRODUCTS = ["Jet A-1", "AGO / Diesel", "PMS", "Marine Fuels", "Fuel Oil", "Lubricants", "Other Petroleum Product"];
const UNITS = ["Litres", "Metric Tonnes", "Barrels", "US Gallons"];
const FREQUENCIES = ["Weekly", "Bi-weekly", "Monthly", "Quarterly", "Custom"];

export default function NewRequest() {
  const navigate = useNavigate();
  const [type, setType] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([{ name: "", volume: "" }]);
  const [form, setForm] = useState({
    product: "", quantity: "", unit: "Litres", delivery_location: "", required_date: "",
    start_date: "", frequency: "Monthly", expected_duration: "", commercial_arrangement: "",
    specs: "", existing_supplier: "", country: "", notes: "", current_supplier: "", current_price: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const recurringLike = type === "recurring" || type === "multi_location";
  const oneTimeLike = type === "one_time" || type === "market_check" || type === "international";

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (type === "multi_location") fd.append("locations", JSON.stringify(locations.filter((l) => l.name)));
      fd.append("num_locations", type === "multi_location" ? String(locations.filter((l) => l.name).length) : "");
      files.forEach((f) => fd.append("files", f));
      const res = await api.post("/portal/requests", fd);
      toast.success("Request submitted", { description: `Reference ${res.data.request_ref}` });
      navigate(`/portal/requests/${res.data.id}`);
    } catch (err) {
      toast.error("Submission failed", { description: fmtErr(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-request-page" className="max-w-3xl">
      <button onClick={() => navigate("/portal")} className="text-sm text-white/45 hover:text-gold transition-colors mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4 inline mr-1" /> Dashboard
      </button>
      <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white">
        New <span className="text-gold">Procurement Request</span>
      </h1>
      <p className="text-sm text-white/50 mt-2 mb-8">What type of procurement do you need?</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="request-type-select">
        {TYPES.map((t) => (
          <button
            key={t.value}
            data-testid={`request-type-${t.value.replace("_", "-")}`}
            onClick={() => setType(t.value)}
            className={`text-left rounded-xl border px-5 py-5 transition-all duration-300 ${
              type === t.value ? "border-gold/60 bg-gold/10 shadow-[0_0_24px_rgba(217,164,65,0.12)]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
            }`}
          >
            <span className={`block font-outfit ${type === t.value ? "text-gold" : "text-white/85"}`}>{t.label}</span>
            <span className="block text-xs text-white/40 mt-1">{t.hint}</span>
          </button>
        ))}
      </div>

      {type && (
        <Card testId="new-request-form-card">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="new-request-form">
            <Field label="Product" required>
              <select required value={form.product} onChange={set("product")} className={inputCls} data-testid="nr-product-select">
                <option value="" disabled>Select a product</option>
                {PRODUCTS.map((p) => <option key={p} value={p} className="bg-surface">{p}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={recurringLike ? "Est. Monthly Volume" : "Quantity"} required={type !== "multi_location"}>
                <input type="number" min="1" value={form.quantity} onChange={set("quantity")} required={type !== "multi_location"} className={inputCls} placeholder="e.g. 500,000" data-testid="nr-quantity-input" />
              </Field>
              <Field label="Unit">
                <select value={form.unit} onChange={set("unit")} className={inputCls} data-testid="nr-unit-select">
                  {UNITS.map((u) => <option key={u} value={u} className="bg-surface">{u}</option>)}
                </select>
              </Field>
            </div>

            {type === "multi_location" && (
              <div className="sm:col-span-2">
                <span className="block text-[11px] tracking-[0.2em] uppercase text-white/45 mb-2">Delivery Locations &amp; Volumes <span className="text-gold">*</span></span>
                <div className="space-y-3" data-testid="nr-locations">
                  {locations.map((loc, i) => (
                    <div key={i} className="flex gap-3">
                      <input value={loc.name} onChange={(e) => setLocations((ls) => ls.map((l, j) => (j === i ? { ...l, name: e.target.value } : l)))} className={inputCls} placeholder="Location (e.g. Lagos)" data-testid={`nr-location-name-${i}`} />
                      <input value={loc.volume} onChange={(e) => setLocations((ls) => ls.map((l, j) => (j === i ? { ...l, volume: e.target.value } : l)))} className={inputCls} placeholder={`Volume (${form.unit})`} data-testid={`nr-location-volume-${i}`} />
                      <button type="button" onClick={() => setLocations((ls) => ls.filter((_, j) => j !== i))} className="text-white/35 hover:text-red-400 shrink-0 px-1" aria-label="Remove location" data-testid={`nr-location-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setLocations((ls) => [...ls, { name: "", volume: "" }])} className="inline-flex items-center gap-2 text-sm text-gold hover:text-goldbright transition-colors" data-testid="nr-add-location">
                    <Plus className="w-4 h-4" /> Add location
                  </button>
                </div>
              </div>
            )}

            {type !== "multi_location" && (
              <Field label="Delivery Location" required>
                <input required value={form.delivery_location} onChange={set("delivery_location")} className={inputCls} placeholder="City, depot, airport or port" data-testid="nr-location-input" />
              </Field>
            )}
            {(type === "one_time" || type === "market_check") && (
              <Field label="Required Delivery Date">
                <input type="date" value={form.required_date} onChange={set("required_date")} className={inputCls} data-testid="nr-date-input" />
              </Field>
            )}
            {type === "market_check" && (
              <>
                <Field label="Current Supplier (optional)">
                  <input value={form.current_supplier} onChange={set("current_supplier")} className={inputCls} placeholder="Who supplies you today" data-testid="nr-current-supplier-input" />
                </Field>
                <Field label="Current Quoted Price (optional)">
                  <input value={form.current_price} onChange={set("current_price")} className={inputCls} placeholder="e.g. ₦1,720 / litre" data-testid="nr-current-price-input" />
                </Field>
              </>
            )}
            {type === "international" && (
              <>
                <Field label="Required Delivery Date">
                  <input type="date" value={form.required_date} onChange={set("required_date")} className={inputCls} data-testid="nr-date-input" />
                </Field>
                <Field label="Country of Operation">
                  <input value={form.country} onChange={set("country")} className={inputCls} placeholder="e.g. Canada" data-testid="nr-country-input" />
                </Field>
              </>
            )}
            {recurringLike && (
              <>
                <Field label="Frequency">
                  <select value={form.frequency} onChange={set("frequency")} className={inputCls} data-testid="nr-frequency-select">
                    {FREQUENCIES.map((f) => <option key={f} value={f} className="bg-surface">{f}</option>)}
                  </select>
                </Field>
                <Field label="Start Date">
                  <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="nr-start-date-input" />
                </Field>
                <Field label="Expected Duration">
                  <input value={form.expected_duration} onChange={set("expected_duration")} className={inputCls} placeholder="e.g. 12 months" data-testid="nr-duration-input" />
                </Field>
                <Field label="Preferred Delivery Schedule / Arrangement">
                  <input value={form.commercial_arrangement} onChange={set("commercial_arrangement")} className={inputCls} placeholder="e.g. monthly contract, per-delivery" data-testid="nr-arrangement-input" />
                </Field>
                <Field label="Existing Supplier (if applicable)">
                  <input value={form.existing_supplier} onChange={set("existing_supplier")} className={inputCls} placeholder="Current supplier, if any" data-testid="nr-existing-supplier-input" />
                </Field>
              </>
            )}

            <div className="sm:col-span-2">
              <Field label="Product Specifications">
                <textarea rows={3} value={form.specs} onChange={set("specs")} className={`${inputCls} resize-none`} placeholder="Grades, standards, certificates required…" data-testid="nr-specs-input" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Additional Requirements">
                <textarea rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} placeholder="Timelines, split volumes, anything else Ajemi should know." data-testid="nr-notes-input" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <FilePicker files={files} setFiles={setFiles} testId="nr-file-upload" />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full px-8 py-4 hover:bg-goldbright hover:shadow-[0_0_40px_rgba(217,164,65,0.45)] transition-all disabled:opacity-60" data-testid="nr-submit-btn">
                {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>) : "Submit Procurement Request"}
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
